import {
  APPROVAL_KIND_REQUIRED_STAGES,
  APPROVAL_KIND_STAGE_APPROVER_ROLES,
  ROLE_OPS_ADMIN,
  ROLE_OPS_CUSTOMER_MANAGER,
  ROLE_OPS_FINANCE_MANAGER,
  ROLE_OPS_RUNTIME_OPERATOR,
  ROLE_OPS_SALES_LEAD,
  ROLE_OPS_SUPER_ADMIN,
  ROLE_OPS_SUPPORT,
  ROLE_OPS_SUPPLY_DEMAND_MANAGER,
  type OpsPermissionRole,
} from '@nadoutong/contracts';
import type { I18nKey, TParams } from '../../i18n/index.ts';

/**
 * Apply-dialog approval-chain copy (router#2085).
 *
 * Stage count and per-stage roles come from the same constants that stamp
 * `approval_requests.required_stages`. Role names go through i18n so a
 * registry edit cannot leave the dialog describing a chain that no longer
 * exists.
 */

export type CreditApplyApprovalKind =
  | 'credit_application'
  | 'temporary_credit_application';

type Translate = (key: I18nKey, params?: TParams) => string;

export function opsRoleLabelKey(role: OpsPermissionRole): I18nKey {
  switch (role) {
    case ROLE_OPS_SUPER_ADMIN:
      return 'permissions.role.label.super';
    case ROLE_OPS_ADMIN:
      return 'permissions.role.label.admin';
    case ROLE_OPS_SUPPORT:
      return 'permissions.role.label.support';
    case ROLE_OPS_SALES_LEAD:
      return 'permissions.role.label.lead';
    case ROLE_OPS_CUSTOMER_MANAGER:
      return 'permissions.role.label.customer';
    case ROLE_OPS_FINANCE_MANAGER:
      return 'permissions.role.label.finance';
    case ROLE_OPS_SUPPLY_DEMAND_MANAGER:
      return 'permissions.role.label.supply';
    case ROLE_OPS_RUNTIME_OPERATOR:
      return 'permissions.role.label.runtime';
  }
}

export function renderApprovalChain(
  stageRoles: readonly (readonly OpsPermissionRole[])[],
  t: Translate,
): string {
  const roleSep = t('credit.apply.approval.role_sep');
  const names = (roles: readonly OpsPermissionRole[]): string =>
    roles.map((role) => t(opsRoleLabelKey(role))).join(roleSep);
  if (stageRoles.length <= 1) {
    const first = stageRoles[0];
    return first === undefined ? '' : names(first);
  }
  const parts: string[] = [];
  for (let index = 0; index < stageRoles.length; index += 1) {
    const roles = stageRoles[index];
    if (roles === undefined) continue;
    parts.push(
      t('credit.apply.approval.stage', {
        n: String(index + 1),
        role: names(roles),
      }),
    );
  }
  return parts.join(t('credit.apply.approval.stage_sep'));
}

export function formatApprovalChainDescription(
  kind: CreditApplyApprovalKind,
  t: Translate,
  extra?: TParams,
): string {
  const stages = APPROVAL_KIND_REQUIRED_STAGES[kind];
  const chain = renderApprovalChain(APPROVAL_KIND_STAGE_APPROVER_ROLES[kind], t);
  const params: TParams = extra === undefined ? { stages, chain } : { ...extra, stages, chain };
  if (kind === 'temporary_credit_application') {
    return t('credit.temporary.apply.description', params);
  }
  return t('credit.apply.description', params);
}
